// ヘッダー関連の 3 機能（fixedHeader / drawer / megaMenu）。
// MicroModal でドロワー・メガメニューを制御し、Lenis でスクロールをロックする。
//
// ============================================
// HTML 構造例:
// ============================================
// <header class="js-header">
//   <button class="js-drawer-hamburger" aria-expanded="false">メニュー</button>
//   <div id="navigation" class="js-drawer-navigation" aria-hidden="true">...</div>
// </header>
// <div class="js-hero">...</div>
// ============================================
// CSS 注意:
// is-active / is-open 等の表示・トランジションは CSS 側で定義する。

import MicroModal from 'micromodal';
import { lenis } from './Scroll';
import { rafThrottle } from '../utils/rafThrottle';

const CONFIG = {
  headerSelector: '.js-header',                    // 固定ヘッダー
  heroSelector: '.js-hero',                        // ヒーロー領域
  hamburgerSelector: '.js-drawer-hamburger',       // ハンバーガーボタン
  navigationSelector: '.js-drawer-navigation',     // ドロワーナビ
  drawerMenuWrapSelector: '.l-drawer__nav',         // ドロワーメニューラッパー
  drawerMenuSelector: '.l-drawer__list',            // ドロワーメニューリスト
  drawerLinkSelector: '.l-drawer__link',            // ドロワー内リンク
  wideHamburgerProperty: '--wide-hamburger',        // 常時ハンバーガー表示の CSS 変数
  hamburgerBreakpointProperty: '--hamburger-breakpoint', // ハンバーガー切替ブレークポイント
  breakpointFallback: 1024,                       // ブレークポイントのフォールバック値
  breakpointEpsilon: 0.02,                          // メディアクエリの境界補正値
  header_activeClass: 'is-active',                // ヒーロー通過後に fixed ヘッダーを表示
  header_hiddenClass: 'is-hidden',                  // スクロール開始直後にヘッダーを隠す
  header_openClass: 'is-open',                      // ドロワーが開いているとき
  hamburger_openClass: 'is-open',                   // ハンバーガーが開いているとき
  drawer_modalId: 'navigation',                     // header.php の id="navigation" に対応
  navigationLinkSelector: '.l-navigation__link',    // グロナビリンク
  popupTriggerSelector: '.l-navigation__link.js-popup-trigger', // メガメニュートリガー
  popupSelector: '.l-navigation__child',            // メガメニューパネル
  popupChildLinkSelector: '.l-navigation__list-child-link', // メガメニュー内リンク
  navigationContainerSelector: '.l-navigation',     // ナビゲーションコンテナ
};

const setHamburgerState = (hamburger, isOpen) => {
  hamburger.classList.toggle(CONFIG.hamburger_openClass, isOpen);
  hamburger.setAttribute('aria-expanded', String(isOpen));
  hamburger.setAttribute('aria-label', isOpen ? 'メニューを閉じる' : 'メニューを開く');
};

// スクロール量に応じて .js-header の表示状態を切り替える。
// ヘッダー高さ超で is-hidden、ヒーロー（.js-hero）通過で is-active。
function fixedHeader() {
  const header = document.querySelector(CONFIG.headerSelector);
  const hero   = document.querySelector(CONFIG.heroSelector);

  // ヒーローがないページ（下層ページ等）では何もしない
  if (!header || !hero) return;

  // 毎スクロールで計測しないようサイズをキャッシュする
  let headerH = header.clientHeight;
  let heroH   = hero.clientHeight;

  const updateSizeCache = () => {
    headerH = header.clientHeight;
    heroH   = hero.clientHeight;
  };

  const updateHeaderState = () => {
    const scrollY = window.scrollY;

    header.classList.toggle(CONFIG.header_activeClass, scrollY > heroH);
    header.classList.toggle(CONFIG.header_hiddenClass, scrollY > headerH && scrollY <= heroH);
  };

  // リサイズ時は clientHeight の再計測も rAF 内にまとめて強制レイアウトを防ぐ
  const requestUpdate       = rafThrottle(updateHeaderState);
  const requestResizeUpdate = rafThrottle(() => {
    updateSizeCache();
    updateHeaderState();
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestResizeUpdate, { passive: true });

  // ページ途中からロードされた場合に備えて初期状態を反映する
  updateHeaderState();
}

// ハンバーガーメニューの開閉。開いている間は Lenis のスクロールをロックする。
// CSS 変数 --wide-hamburger（'false' でスマホのみ）と --hamburger-breakpoint で挙動を切り替える。
function drawer() {
  // drawer・megaMenu の両方で使うが、初期化はここで一度だけ
  MicroModal.init();

  const header     = document.querySelector(CONFIG.headerSelector);
  const hamburger  = document.querySelector(CONFIG.hamburgerSelector);
  const navigation = document.querySelector(CONFIG.navigationSelector);

  if (!hamburger || !navigation) return;

  const menuWrap = navigation.querySelector(CONFIG.drawerMenuWrapSelector);
  const menu     = navigation.querySelector(CONFIG.drawerMenuSelector);

  if (!menu || !menuWrap) return;

  const styles        = getComputedStyle(document.documentElement);
  const wideHamburger = styles.getPropertyValue(CONFIG.wideHamburgerProperty).trim();
  const breakpoint    = Number.parseFloat(styles.getPropertyValue(CONFIG.hamburgerBreakpointProperty)) || CONFIG.breakpointFallback;
  const mediaQuery    = window.matchMedia(`(max-width: ${breakpoint - CONFIG.breakpointEpsilon}px)`);

  // ドロワー内でスクロールしても背景がスクロールしないようにする
  const stopScrollPropagation = (e) => e.stopPropagation();
  const passiveOpts           = { passive: true };
  let scrollBound             = false;

  const bindDrawerScroll = () => {
    if (scrollBound) return;
    menuWrap.addEventListener('wheel', stopScrollPropagation, passiveOpts);
    menuWrap.addEventListener('touchmove', stopScrollPropagation, passiveOpts);
    scrollBound = true;
  };

  const unbindDrawerScroll = () => {
    if (!scrollBound) return;
    menuWrap.removeEventListener('wheel', stopScrollPropagation, passiveOpts);
    menuWrap.removeEventListener('touchmove', stopScrollPropagation, passiveOpts);
    scrollBound = false;
  };

  // 'false' ならスマホのみハンバーガー。それ以外（'true' や未設定）は常に表示
  if (wideHamburger === 'false') {
    applyResponsiveDrawer(mediaQuery);

    // addListener は非推奨のため addEventListener を優先する
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', applyResponsiveDrawer);
    } else {
      mediaQuery.addListener(applyResponsiveDrawer); // Safari 旧版フォールバック
    }

    // 画面幅に応じてハンバーガーの有効/無効を切り替える
    function applyResponsiveDrawer(mq) {
      if (mq.matches) {
        hamburger.addEventListener('click', onHamburgerClick);
        bindDrawerScroll();
        navigation.setAttribute('aria-hidden', 'true');
      } else {
        if (navigation.getAttribute('aria-hidden') === 'false') {
          MicroModal.close(CONFIG.drawer_modalId);
        }
        navigation.querySelectorAll('a, button').forEach((el) => el.blur());
        hamburger.blur();
        navigation.setAttribute('aria-hidden', 'true');
        lenis.start();
        setHamburgerState(hamburger, false);
        unbindDrawerScroll();
        hamburger.removeEventListener('click', onHamburgerClick);
      }
    }
  } else {
    hamburger.addEventListener('click', onHamburgerClick);
    if (wideHamburger === 'true') {
      bindDrawerScroll();
    }
    navigation.setAttribute('aria-hidden', 'true');
  }

  function onHamburgerClick() {
    const isOpen = navigation.getAttribute('aria-hidden') === 'false';
    isOpen ? closeDrawer() : openDrawer();
  }

  function openDrawer() {
    header?.classList.add(CONFIG.header_openClass);
    setHamburgerState(hamburger, true);
    lenis.stop();

    MicroModal.show(CONFIG.drawer_modalId, {
      onClose: () => {
        // 外側クリックや ESC で閉じた場合も状態をリセットする
        header?.classList.remove(CONFIG.header_openClass);
        setHamburgerState(hamburger, false);
        lenis.start();
      },
    });
  }

  function closeDrawer() {
    setHamburgerState(hamburger, false);
    header?.classList.remove(CONFIG.header_openClass);
    lenis.start();
    MicroModal.close(CONFIG.drawer_modalId);
  }

  // ドロワー内のリンクをクリックしたら自動で閉じる
  navigation.querySelectorAll(CONFIG.drawerLinkSelector).forEach((link) => {
    link.addEventListener('click', () => {
      if (navigation.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });
  });
}

// PC グローバルナビのメガメニュー（ホバー開閉）。
// .js-popup-trigger の aria-controls と .l-navigation__child の id を対応させる。
function megaMenu() {
  const allLinks = document.querySelectorAll(CONFIG.navigationLinkSelector);
  const triggers = document.querySelectorAll(CONFIG.popupTriggerSelector);
  const popups   = document.querySelectorAll(CONFIG.popupSelector);

  if (allLinks.length === 0 || triggers.length === 0 || popups.length === 0) return;

  // 一括クローズ用に aria-controls の ID を集めておく
  const managedModalIds = [];
  allLinks.forEach((link) => {
    const id = link.getAttribute('aria-controls');
    if (id) managedModalIds.push(id);
  });

  // ホバー / フォーカス / Escape で共用する
  const closeAllPopups = () => {
    managedModalIds.forEach((id) => {
      const modal = document.getElementById(id);
      if (modal?.classList.contains('is-open')) MicroModal.close(id);
    });
  };

  // ナビリンクにホバー / フォーカスしたら他のポップアップを閉じる。
  // トリガー自身は後段で「自分のポップアップを開く」処理を行うため focus からは除外する
  allLinks.forEach((link) => {
    link.addEventListener('mouseenter', closeAllPopups);
    if (!link.classList.contains('js-popup-trigger')) {
      link.addEventListener('focus', closeAllPopups);
    }
  });

  popups.forEach((popup) => {
    popup.addEventListener('mouseleave', () => {
      const id = popup.id;
      if (id) MicroModal.close(id);
    });
  });

  const openPopup = (id, { keepFocus = false } = {}) => {
    const modal = document.getElementById(id);
    if (!modal || modal.classList.contains('is-open')) return;

    MicroModal.show(id);

    // ホバー時は内部リンクがフォーカスを奪わないよう blur する（キーボード操作時は行わない）
    if (!keepFocus) {
      const firstLink = modal.querySelector(CONFIG.popupChildLinkSelector);
      firstLink?.blur();
    }
  };

  triggers.forEach((trigger) => {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return;

    trigger.addEventListener('mouseenter', () => openPopup(id));
    trigger.addEventListener('focus', () => {
      closeAllPopups();
      openPopup(id, { keepFocus: true });
    });
  });

  // Escape で閉じ、開いていたトリガーへフォーカスを戻す
  const navContainer = triggers[0].closest(CONFIG.navigationContainerSelector) || document;
  navContainer.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openTrigger = Array.from(triggers).find((t) => {
      const cid = t.getAttribute('aria-controls');
      return cid && document.getElementById(cid)?.classList.contains('is-open');
    });
    if (!openTrigger) return;
    closeAllPopups();
    openTrigger.focus();
  });
}

export { fixedHeader, drawer, megaMenu };
